import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { SEED_TO_HARVEST, TECHNOLOGY, INTEGRATED_FLOW, IMAGES } from "@/data/content";
import { motion } from "framer-motion";
import {
  Droplets, Gauge, Activity, ClipboardList, TrendingUp, CalendarClock, Cpu, ArrowRight,
} from "lucide-react";

const TECH_ICONS = [Droplets, Gauge, Activity, ClipboardList, TrendingUp, CalendarClock, Cpu];

function FlowRail({ steps, testId }) {
  return (
    <div className="relative" data-testid={testId}>
      <div className="flex gap-3 overflow-x-auto pb-4 lg:grid lg:grid-flow-col lg:auto-cols-fr lg:overflow-visible">
        {steps.map((step, i) => (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="relative min-w-[150px] lg:min-w-0"
          >
            <div className="h-full rounded-2xl border border-white/10 bg-[#071728]/80 p-4 hover:border-cyan-400/40 transition-colors">
              <span className="font-mono-tech text-xs text-cyan-400/70">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 text-sm font-semibold text-slate-100 leading-snug">{step}</p>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 h-4 w-4 text-cyan-400/60 z-10" />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function Process() {
  return (
    <section id="process" className="relative py-20 lg:py-32 bg-[#04121f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Seed to Harvest */}
        <Reveal>
          <SectionHeading number="06" eyebrow="From Seed to Harvest" title="A clear, managed production journey."
            subtitle="Every stage is planned and monitored — from site planning through to reaching the market." />
        </Reveal>
        <div className="mt-10">
          <FlowRail steps={SEED_TO_HARVEST} testId="seed-to-harvest-flow" />
        </div>

        {/* Technology */}
        <div id="technology" className="mt-24 lg:mt-32 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-emerald-500/10 to-cyan-500/10 blur-2xl" />
              <img src={IMAGES.technology} alt="Technology-driven aquaculture" className="relative h-[420px] w-full object-cover rounded-[1.75rem] border border-white/10" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <SectionHeading number="07" eyebrow="Technology-Driven" title="Aquaculture guided by data and monitoring." />
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {TECHNOLOGY.map((t, i) => {
                const Icon = TECH_ICONS[i % TECH_ICONS.length];
                return (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="flex items-center gap-3 rounded-xl glass px-4 py-3"
                  >
                    <span className="grid place-items-center h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-slate-200">{t}</span>
                  </motion.div>
                );
              })}
            </div>
          </Reveal>
        </div>

        {/* Integrated Aquaculture */}
        <div id="integrated" className="mt-24 lg:mt-32 relative overflow-hidden rounded-[2rem] border border-white/10">
          <img src={IMAGES.integratedBg} alt="Integrated aquaculture" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#030D18]/85" />
          <div className="relative p-8 sm:p-12">
            <Reveal>
              <SectionHeading number="08" eyebrow="Integrated Aquaculture" title="Activities connected to create value."
                subtitle="Different parts of the operation are linked so they can support one another across the chain." />
            </Reveal>
            <div className="mt-10">
              <FlowRail steps={INTEGRATED_FLOW} testId="integrated-flow" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
